import { HttpClient } from '@angular/common/http';
import { Component, inject, signal } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { Router, RouterOutlet } from '@angular/router';
import { finalize } from 'rxjs';
import { environment } from '../environments/environment';
import { UiIconComponent } from './ui-icon.component';

type Origine = 'BENEFICIAIRE' | 'PRESTATAIRE' | 'GARANT' | 'GESTIONNAIRE' | 'SOUSCRIPTEUR';
interface LoginResponse { hasError: boolean; status?: { message?: string }; item?: Record<string, unknown>; }

@Component({ selector: 'app-root', imports: [RouterOutlet, ReactiveFormsModule, UiIconComponent], templateUrl: './app.html', styleUrl: './app.scss' })
export class App {
  private readonly http = inject(HttpClient);
  private readonly fb = inject(FormBuilder);
  private readonly router = inject(Router);
  protected readonly environmentName = environment.name;
  protected readonly loading = signal(false);
  protected readonly errorMessage = signal('');
  protected readonly successMessage = signal('');
  protected readonly authenticated = signal(Boolean(localStorage.getItem('medicareleeUser')));
  protected readonly viewMode = signal<'spaces' | 'other' | 'login'>('spaces');
  protected readonly spaces: ReadonlyArray<{ label: string; value: Origine; icon: 'insured' | 'provider' | 'guarantor' | 'manager'; description: string }> = [
    { label: 'Assuré', value: 'BENEFICIAIRE', icon: 'insured', description: 'Ma couverture, ma famille et mes soins' },
    { label: 'Prestataire', value: 'PRESTATAIRE', icon: 'provider', description: 'Prises en charge et prestations' },
  ];
  protected readonly otherSpaces = [
    { label: 'Gestionnaire', value: 'GESTIONNAIRE' as Origine, icon: 'manager' as const, description: 'Pilotage des opérations santé' },
    { label: 'Garant', value: 'GARANT' as Origine, icon: 'guarantor' as const, description: 'Portefeuille et suivi des contrats' },
    { label: 'Souscripteur', value: 'SOUSCRIPTEUR' as Origine, icon: 'manager' as const, description: 'Contrats, effectifs et cotisations' }
  ];
  protected readonly loginForm = this.fb.nonNullable.group({
    origine: ['BENEFICIAIRE' as Origine, Validators.required], identifier: ['', Validators.required], password: ['', Validators.required]
  });
  protected readonly simplifiedLoginForm = this.fb.nonNullable.group({
    matricule: ['', Validators.required], dateNaissance: ['', Validators.required]
  });

  constructor() {
    if (window.location.search) window.history.replaceState({}, document.title, window.location.pathname);
    if (this.authenticated() && window.location.pathname === '/') void this.router.navigateByUrl('/accueil');
  }

  protected selectSpace(value: Origine): void {
    this.loginForm.controls.origine.setValue(value);
    this.errorMessage.set('');
    this.viewMode.set('login');
  }

  protected openOtherSpaces(): void { this.viewMode.set('other'); }

  protected selectedSpaceLabel(): string {
    return [...this.spaces, ...this.otherSpaces].find(space => space.value === this.loginForm.controls.origine.value)?.label || '';
  }

  protected backToSpaces(): void {
    this.viewMode.set('spaces');
    this.errorMessage.set('');
    this.loginForm.controls.password.reset();
    this.simplifiedLoginForm.reset();
  }

  protected submit(): void {
    this.errorMessage.set(''); this.successMessage.set('');
    if (this.loginForm.invalid || this.loading()) { this.loginForm.markAllAsTouched(); return; }
    const value = this.loginForm.getRawValue();
    const request = { data: { login: value.identifier.trim(), password: value.password, origine: value.origine } };
    this.loading.set(true);
    this.http.post<LoginResponse>(`${environment.apiUrl}/user/login`, request).pipe(finalize(() => this.loading.set(false))).subscribe({
      next: response => {
        if (response.hasError || !response.item) { this.errorMessage.set(response.status?.message || 'Connexion impossible. Vérifiez vos informations.'); return; }
        localStorage.setItem('medicareleeUser', JSON.stringify(response.item));
        this.authenticated.set(true);
        void this.router.navigateByUrl('/accueil');
      },
      error: error => this.errorMessage.set(error?.error?.status?.message || 'Le service est momentanément indisponible.')
    });
  }

  protected submitSimplifiedLogin(): void {
    this.errorMessage.set('');
    if (this.simplifiedLoginForm.invalid || this.loading()) { this.simplifiedLoginForm.markAllAsTouched(); return; }
    const value = this.simplifiedLoginForm.getRawValue();
    const request = { data: { matricule: value.matricule.trim(), date_naissance: value.dateNaissance } };
    this.loading.set(true);
    this.http.post<LoginResponse>(`${environment.apiUrl}/beneficiaire/connexionSimplifiee`, request).pipe(finalize(() => this.loading.set(false))).subscribe({
      next: response => {
        if (response.hasError || !response.item) { this.errorMessage.set(response.status?.message || 'Matricule ou date de naissance incorrect(e).'); return; }
        localStorage.setItem('medicareleeUser', JSON.stringify(response.item));
        this.authenticated.set(true);
        void this.router.navigateByUrl('/accueil');
      },
      error: error => this.errorMessage.set(error?.error?.status?.message || 'Le service est momentanément indisponible.')
    });
  }
}
